import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { applyPhoneUserChanges, mergePendingChanges, } from '@/shared/model/qstat-phone-users-event.model';
import { buildPhoneUsersQueryParams, extractPhoneUsersFilterReference, normalizePhoneUsersResponse, } from '../model/phone-users.model';
import { readSpringTotalCount } from '../utils/spring-api';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const phoneUsersApiUrl = API_ROUTES.qstat.phoneUsers;
const emptyFilterReference = () => ({
    branches: [],
    agentNames: [],
    displayStatuses: [],
});
const initialState = {
    loading: false,
    errorMessage: null,
    asteriskConnected: false,
    wsConnectionError: null,
    wsLastUpdated: null,
    pendingWsChanges: [],
    phoneUsers: [],
    totalItems: 0,
    filterReference: emptyFilterReference(),
    lastUpdated: null,
};
async function fetchPhoneUsersPage(query) {
    const params = buildPhoneUsersQueryParams(query);
    const qs = params.toString();
    const url = qs ? `${phoneUsersApiUrl}?${qs}` : phoneUsersApiUrl;
    const res = await axios.get(url);
    const snapshot = normalizePhoneUsersResponse(res.data);
    const data = res.data;
    const bodyTotal = data && typeof data === 'object' && typeof data.totalElements === 'number'
        ? data.totalElements
        : undefined;
    const total = readSpringTotalCount(res.headers) ?? bodyTotal ?? snapshot.users.length;
    return { snapshot, total };
}
export const getPhoneUsers = createAsyncThunk('realtime/fetch_phone_users', async (args) => {
    const query = {
        page: 0,
        size: 15,
        ...args?.query,
    };
    const { snapshot, total } = await fetchPhoneUsersPage(query);
    let filterUsers = snapshot.users;
    if (args?.captureFilterReference && total > snapshot.users.length) {
        const all = await fetchPhoneUsersPage({ ...query, page: 0, size: Math.max(total, 1) });
        filterUsers = all.snapshot.users;
    }
    return {
        users: snapshot.users,
        asteriskConnected: snapshot.asteriskConnected,
        total,
        captureFilterReference: Boolean(args?.captureFilterReference),
        filterUsers,
    };
}, { serializeError: serializeAxiosError });
export const realtimeSlice = createSlice({
    name: 'realtime',
    initialState: initialState,
    reducers: {
        clearRealtime(state) {
            state.phoneUsers = [];
            state.totalItems = 0;
            state.filterReference = emptyFilterReference();
            state.errorMessage = null;
            state.asteriskConnected = false;
            state.wsConnectionError = null;
            state.wsLastUpdated = null;
            state.pendingWsChanges = [];
            state.lastUpdated = null;
        },
        applyPhoneUsersWsEvent(state, action) {
            const event = action.payload;
            state.asteriskConnected = event.asteriskConnected;
            state.wsConnectionError = event.connectionError;
            if (!event.changes.length) {
                state.wsLastUpdated = new Date().toISOString();
                return;
            }
            if (state.phoneUsers.length === 0) {
                state.pendingWsChanges = mergePendingChanges(state.pendingWsChanges, event.changes);
                state.wsLastUpdated = new Date().toISOString();
                return;
            }
            const { users } = applyPhoneUserChanges(state.phoneUsers, event.changes);
            state.phoneUsers = users;
            state.wsLastUpdated = new Date().toISOString();
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getPhoneUsers.fulfilled, (state, action) => {
            state.loading = false;
            let users = action.payload.users;
            if (state.pendingWsChanges.length > 0) {
                users = applyPhoneUserChanges(users, state.pendingWsChanges).users;
                state.pendingWsChanges = [];
            }
            state.phoneUsers = users;
            state.totalItems = action.payload.total;
            state.asteriskConnected = action.payload.asteriskConnected;
            state.wsConnectionError = null;
            state.lastUpdated = new Date().toISOString();
            state.errorMessage = null;
            if (action.payload.captureFilterReference) {
                state.filterReference = extractPhoneUsersFilterReference(action.payload.filterUsers);
            }
        })
            .addMatcher(isPending(getPhoneUsers), (state) => {
            state.loading = true;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(getPhoneUsers), (state, action) => {
            state.loading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { clearRealtime, applyPhoneUsersWsEvent } = realtimeSlice.actions;
export default realtimeSlice.reducer;
